import { ContentfulFAQ, getContentfulFAQ } from 'api/contentful/getContentfulFAQ'
import {
  ContentfulMediaMentions,
  getContentfulMediaMentions,
} from 'api/contentful/getContentfulMediaMention'
import PlanPerks from 'components/content/planPerks/PlanPerks'
import { DashlaneHeroSection } from 'components/dashlane/DashlaneHeroSection'
import { DashlaneMentionsSection } from 'components/dashlane/DashlaneMentionsSection'
import { DashlanePageSections } from 'components/dashlane/DashlanePageSections'
import { FaqTwoColumns } from 'components/faqTwoColumn/FaqTwoColumn'
import { Footer } from 'components/footer/Footer'
import { TopNavigationLandingPage } from 'components/header/topNavigationLandingPage/TopNavigationLandingPage'
import { Layout } from 'components/layout/Layout'
import { PageMeta } from 'components/PageMeta'
import { PageTitle } from 'components/PageTitle'
import { Signup, ToneContext } from 'components/signup/Signup'
import { Spacer } from 'components/spacer/Spacer'
import { GetStaticProps } from 'next'
import { useTranslation } from 'next-i18next'
import React from 'react'
import { REVALIDATE_SECS } from 'utils/constants'
import { getContentfulImages } from 'utils/contentful/getPageImages'
import { numberRange } from 'utils/general'
import { getIntlProps } from 'utils/i18n/getIntlProps'
import { mapKeysToTrans } from 'utils/i18n/mapKeysToTrans'

interface DashlanePageStaticProps {
  faq: ContentfulFAQ
  mediaMentions: ContentfulMediaMentions
}

export default function Page(props: DashlanePageStaticProps) {
  const { t } = useTranslation('dashlane')
  const { mediaMentions } = props

  const signupPerks = mapKeysToTrans(
    t,
    numberRange(4).map((i) => `dashlane.section_signup.perk_${i}` as const)
  )

  return (
    <Layout
      header={<TopNavigationLandingPage variant="full" noSpacingBelow />}
      footer={<Footer variant="full" />}
    >
      <PageTitle title={t('dashlane.meta_title')} hideDefaultSuffix />
      <PageMeta description={t('dashlane.meta_description')} image="/images/preview/dashlane.png" />

      <DashlaneHeroSection />

      <Spacer size={3} />

      <DashlaneMentionsSection mediaMentionsWithCaptionCollection={mediaMentions} limit={5} />

      <Spacer size={[2, 6]} />

      <DashlanePageSections />

      <Spacer size={[2, 6]} />

      <PlanPerks />

      <Spacer size={[2, 6]} />

      <FaqTwoColumns content={props.faq} title={t('dashlane.faq_title')}></FaqTwoColumns>

      <Spacer size={[2, 6]} />

      <ToneContext.Provider value="dark">
        <Signup
          caption={t('dashlane.section_signup.caption')}
          title={t('dashlane.section_signup.title')}
          perks={signupPerks}
        />
      </ToneContext.Provider>
    </Layout>
  )
}

export const getStaticProps: GetStaticProps<DashlanePageStaticProps> = async (ctx) => {
  const mediaMentions = await getContentfulMediaMentions('global')

  const faq = await getContentfulFAQ('dashlane')

  const props: DashlanePageStaticProps = {
    ...(await getContentfulImages()),
    ...(await getIntlProps(ctx, ['dashlane', 'pricing_plan'])),
    faq,
    mediaMentions,
  }

  return { props, revalidate: REVALIDATE_SECS }
}
